import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ViewEncapsulation,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  NgZone,
} from '@angular/core';
import {
  ComplianceData,
  ComplianceElement,
} from '../../../../../shared/models/compliance-data.model';
import {
  COMMON_IMPORTS,
  MATERIAL_IMPORTS,
  PIPES_IMPORTS,
} from '../../../../../shared/imports/common-imports';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, Plugin } from 'chart.js';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-compliance-wheel',
  standalone: true,
  imports: [
    COMMON_IMPORTS,
    PIPES_IMPORTS,
    BaseChartDirective,
    MATERIAL_IMPORTS,
  ],
  templateUrl: './compliance-wheel.component.html',
  styleUrls: ['./compliance-wheel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ComplianceWheelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  @ViewChild('chartLegend', { static: false }) chartLegend!: ElementRef;

  private _data!: ComplianceData;
  @Input()
  set data(value: ComplianceData) {
    this._data = value;
    this.overallScore = this._data.elements.reduce(
      (acc, el) => acc + el.value,
      0,
    );
    this.scheduleChartUpdate();
  }
  get data(): ComplianceData {
    return this._data;
  }

  selectedSegmentIndex: number = -1;
  private indexChangedSubscription!: Subscription;
  @Input() indexChanged: Observable<number> = new Observable<number>();
  @Output('onIndexChange') selectedSegmentIndexChange =
    new EventEmitter<number>();

  overallScore = 0;
  clickLock = false;
  doughnutChartData: ChartData<'doughnut'> = {
    datasets: [],
  };
  private originalRadii: { [key: number]: number } = {}; // Store original inner radii
  private overlaysDrawn = false; // Flag to track if overlays are already drawn
  private resizeObserver: ResizeObserver | null = null; // ResizeObserver for container changes
  private pendingAnimationFrame: number | null = null; // Track pending animation frame
  private isInitialized = false; // Track if component is fully initialized

  constructor(private readonly ngZone: NgZone) {}

  // Custom plugin for segment thickness expansion
  segmentExpansionPlugin: Plugin = {
    id: 'segmentExpansion',
    beforeDatasetDraw: (chart, args) => {
      this.storeOriginalRadii(chart, args);
      if (this.selectedSegmentIndex >= 0) {
        this.modifySelectedSegmentRadius(chart, args);
      }
    },
  };

  // Custom plugin for HTML overlays
  htmlOverlayPlugin: Plugin = {
    id: 'htmlOverlay',
    afterDraw: (chart) => {
      // Only redraw overlays when necessary (not on every hover)
      if (!this.overlaysDrawn) {
        this.drawHtmlOverlays(chart);
        this.overlaysDrawn = true;
      }
    },
  };

  plugins = [this.segmentExpansionPlugin, this.htmlOverlayPlugin];

  options = {
    cutout: '80%',
    animation: {
      duration: 300,
    },
    // Use Chart.js built-in resize debouncing for consistent behavior across refresh rates
    resizeDelay: 100,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    responsive: true,
    maintainAspectRatio: true,
    showtooltips: false,
    tooltips: false,
    interaction: {
      mode: 'point' as const,
    },
    onClick: (event: any) => {
      this.chartMouseInteract(event, true);
    },
    onHover: (event: any) => {
      if (event.type === 'click' || this.clickLock) return; // Ignore click events here
      this.chartMouseInteract(event);
    },
    onResize: () => {
      // Mark overlays for redraw when chart resizes
      this.overlaysDrawn = false;
    },
  };

  ngOnInit() {
    this.indexChangedSubscription = this.indexChanged.subscribe((index) => {
      this.selectedSegmentIndex = index;
      this.setupChartData();
    });
  }

  /**
   * Schedule a chart update using requestAnimationFrame for proper synchronization
   * with the browser's paint cycle. This ensures consistent behavior across different
   * refresh rates (60Hz, 120Hz, 144Hz, etc.)
   */
  private scheduleChartUpdate(): void {
    // Cancel any pending animation frame to avoid multiple updates
    if (this.pendingAnimationFrame !== null) {
      cancelAnimationFrame(this.pendingAnimationFrame);
    }

    // Use requestAnimationFrame to sync with the browser's refresh rate
    // This runs outside Angular zone to avoid unnecessary change detection
    this.ngZone.runOutsideAngular(() => {
      this.pendingAnimationFrame = requestAnimationFrame(() => {
        this.pendingAnimationFrame = null;
        this.ngZone.run(() => {
          this.overlaysDrawn = false;
          if (this.chart?.chart && this.isInitialized) {
            this.setupChartData();
            this.chart.chart.resize();
            this.chart.update('none');
          }
        });
      });
    });
  }

  /**
   * Set up ResizeObserver to detect container size changes.
   * This is more reliable than window:resize for element-level changes.
   */
  private setupResizeObserver(): void {
    if (!this.chartContainer?.nativeElement || this.resizeObserver) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver((entries) => {
        // Only process if we have valid entries
        if (entries.length > 0) {
          // Use requestAnimationFrame to batch with browser paint cycle
          this.scheduleChartUpdate();
        }
      });

      this.resizeObserver.observe(this.chartContainer.nativeElement);
    });
  }

  ngAfterViewInit() {
    this.isInitialized = true;
    this.setupChartData();
    this.setupResizeObserver();
  }

  chartMouseInteract(event: any, clickInteraction = false) {
    if (!this.chart?.chart) return;

    if (
      event.type !== 'click' &&
      event.type !== 'mousemove' &&
      event.type !== 'mouseout'
    )
      return;

    const points = this.chart.chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false,
    );

    if (points.length > 0) {
      const clickedSegmentIndex = points[0].index;

      // Toggle selection: if same segment clicked, deselect; otherwise select new segment
      if (
        this.selectedSegmentIndex === clickedSegmentIndex &&
        clickInteraction &&
        this.clickLock
      ) {
        this.selectedSegmentIndex = -1; // Deselect
        this.clickLock = false; // Release click lock on deselect
      } else {
        this.selectedSegmentIndex = clickedSegmentIndex; // Select new segment
        if (clickInteraction) this.clickLock = true; // Lock hover changes after click selection
      }

      // if mouse left unselect
      if (!this.clickLock && !clickInteraction && event.type === 'mouseout') {
        this.selectedSegmentIndex = -1;
      }

      this.selectedSegmentIndexChange.emit(this.selectedSegmentIndex);

      // Update chart with new selection
      this.setupChartData();
      return;
    }

    if (!clickInteraction) return; //hover does not deselect

    // Clicked outside segments, deselect all
    this.selectedSegmentIndex = -1;
    this.clickLock = false; // Release click lock on deselect
    this.selectedSegmentIndexChange.emit(this.selectedSegmentIndex);
    this.setupChartData();
  }

  private storeOriginalRadii(chart: any, args: any) {
    const meta = chart.getDatasetMeta(args.index);
    if (!meta.data) return;

    meta.data.forEach((element: any, index: number) => {
      if (!(index in this.originalRadii)) {
        this.originalRadii[index] = element.innerRadius;
      }
    });
  }

  private modifySelectedSegmentRadius(chart: any, args: any) {
    const meta = chart.getDatasetMeta(args.index);
    if (!meta.data || this.selectedSegmentIndex < 0) return;

    const selectedElement = meta.data[this.selectedSegmentIndex];
    if (selectedElement && this.selectedSegmentIndex in this.originalRadii) {
      // Use the stored original radius and reduce it to make the segment thicker
      const originalInnerRadius = this.originalRadii[this.selectedSegmentIndex];
      selectedElement.innerRadius = Math.max(0, originalInnerRadius * 0.6);
    }
  }

  private setupChartData() {
    if (!this.chart?.chart?.canvas) {
      return;
    }

    // Clear stored radii when setting up new data
    this.originalRadii = {};
    this.overlaysDrawn = false; // Reset flag to allow redraw with new data

    const values = this.data.elements.map((el) => el.value);
    const colors = this.data.elements.map((el) => el.color);

    this.doughnutChartData.datasets = [
      {
        data: values.map(() => 1), // every segment has same length visually
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverBackgroundColor: colors, // Keep same color on hover
        hoverBorderColor: '#ffffff', // Keep same border color on hover
        hoverBorderWidth: 2, // Keep same border width on hover
      },
    ];

    this.chart.update('none'); // Update without animation
  }

  private drawHtmlOverlays(chart: any) {
    if (!this.chartContainer) return;

    const meta = chart.getDatasetMeta(0);

    // calculate chart area width to center legend properly
    if (this.chartLegend) {
      // Use percentage-based sizing for zoom independence
      const legendSizePercent = 75; // Legend is 75% of the chart container
      this.chartLegend.nativeElement.style.width = `${legendSizePercent}%`;
      this.chartLegend.nativeElement.style.height = `${legendSizePercent}%`;

      // Center the legend using percentages relative to canvas size
      const legendOffsetPercent = (100 - legendSizePercent) / 2; // Center horizontally
      this.chartLegend.nativeElement.style.left = `${legendOffsetPercent}%`;
    }

    // Clear any existing overlays
    this.clearHtmlOverlays();

    meta.data.forEach((segment: any, index: number) => {
      const element = this.data.elements[index];
      const startAngle = segment.startAngle;
      const endAngle = segment.endAngle;
      const midAngle = (startAngle + endAngle) / 2;

      // Calculate position for the HTML overlay (middle of the segment)
      const outerRadius = segment.outerRadius;
      const innerRadius = this.originalRadii[index] || segment.innerRadius;

      const isSelected = index === this.selectedSegmentIndex;
      const radiousAdjustment = isSelected ? 0.3 : 0.5;
      // Position overlay closer to the inner edge for better visibility
      const radius =
        innerRadius + (outerRadius - innerRadius) * radiousAdjustment;

      // Calculate position relative to the canvas internal coordinate system
      const centerX = chart.width / 2;
      const centerY = chart.height / 2;

      const canvasX = centerX + Math.cos(midAngle) * radius;
      const canvasY = centerY + Math.sin(midAngle) * radius;

      // Convert canvas coordinates to percentages relative to the canvas size
      // This makes positioning zoom-independent
      const xPercent = (canvasX / chart.width) * 100;
      const yPercent = (canvasY / chart.height) * 100;

      // Create HTML overlay element
      this.createHtmlOverlay(element, xPercent, yPercent, index);
    });
  }

  private createHtmlOverlay(
    element: ComplianceElement,
    xPercent: number,
    yPercent: number,
    index: number,
  ) {
    if (!this.chartContainer) return;

    const overlay = document.createElement('div');
    const isSelected = index === this.selectedSegmentIndex;
    const sizeClass = isSelected ? 'large' : 'normal';
    overlay.className = `chart-overlay overlay-${index}`;
    overlay.innerHTML = `
      <div class="overlay-content ">
        <span class="element-value ${element.colorName}" ><span class="material-icons ${sizeClass}">${element.icon}</span></span>
      </div>
    `;

    // Use percentage-based positioning relative to the canvas
    overlay.style.left = `${xPercent}%`;
    overlay.style.top = `${yPercent}%`;

    this.chartContainer.nativeElement.appendChild(overlay);
  }

  private clearHtmlOverlays() {
    if (!this.chartContainer) return;

    const overlays =
      this.chartContainer.nativeElement.querySelectorAll('.chart-overlay');
    overlays.forEach((overlay: Element) => overlay.remove());
  }

  getDetailTitle(): string {
    let title = 'Overview';
    if (this.selectedSegmentIndex >= 0) {
      title = this.data.elements[this.selectedSegmentIndex].elementName;
    }
    return title;
  }

  getDetailScore(): number {
    if (this.selectedSegmentIndex >= 0) {
      return this.data.elements[this.selectedSegmentIndex].value;
    }
    return this.overallScore;
  }

  ngOnDestroy() {
    this.indexChangedSubscription.unsubscribe();

    // Clean up pending animation frame
    if (this.pendingAnimationFrame !== null) {
      cancelAnimationFrame(this.pendingAnimationFrame);
      this.pendingAnimationFrame = null;
    }

    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}
